// Importar el tipo DeviceSample
import type { DeviceSample } from '../../DeviceSamples/models/deviceSample'

export interface DeviceImage {
    src: string;
    name: string;
    position: number;
}

export interface DeviceMetadata {
    // Datos estáticos del dispositivo
    expectedMeasurements?: number;
    observations?: string;
    status?: 'connected' | 'disconnected' | 'sync_delay' | 'maintenance';
    
    // Datos específicos del dispositivo
    soilType?: string;
    irrigationType?: string;
    plantVariety?: string;
    installationDepth?: number;
    sensorCount?: number;
    batteryLevel?: number;
    signalStrength?: number;
    lastMaintenanceDate?: Date;
    nextMaintenanceDate?: Date;
    
    [key: string]: string | number | Date | boolean | undefined;
}

export class Device {
    id: string;
    code: string;
    locationId: string;
    name: string;
    extendedName: string;
    description: string;
    slug: string;
    initDate: Date;
    active: boolean;
    lat: number;
    lng: number;
    altitude: number;
    periodicity: number;
    groupId: number;
    alarmed: boolean;
    createdAt: Date;
    updatedAt: Date;
    createdBy: string;
    updatedBy: string;
    
    // Nuevos campos
    images?: DeviceImage[];
    metadata: string;

    private spanishNames?: Record<string, string> = {
        id: "identificador",
        code: "código",
        locationId: "ID de ubicación",
        name: "nombre",
        extendedName: "nombre extendido",
        description: "descripción",
        slug: "slug",
        initDate: "fecha de inicio",
        active: "activo",
        lat: "latitud",
        lng: "longitud",
        altitude: "altitud",
        periodicity: "periodicidad",
        groupId: "ID de grupo",
        alarmed: "alarmado",
        createdAt: "creado en",
        updatedAt: "actualizado en",
        createdBy: "creado por",
        updatedBy: "actualizado por",
        images: "imágenes",
        metadata: "metadatos",
    };

    constructor(data?: Partial<Device>) {
        this.id = data?.id ?? "";
        this.code = data?.code ?? "";
        this.locationId = data?.locationId ?? "";
        this.name = data?.name ?? "";
        this.extendedName = data?.extendedName ?? "";
        this.description = data?.description ?? "";
        this.slug = data?.slug ?? "";
        this.initDate = data?.initDate ? new Date(data.initDate) : new Date();
        this.active = data?.active ?? false;
        this.lat = data?.lat ?? 0;
        this.lng = data?.lng ?? 0;
        this.altitude = data?.altitude ?? 0;
        this.periodicity = data?.periodicity ?? 0;
        this.groupId = data?.groupId ?? 0;
        this.alarmed = data?.alarmed ?? false;
        this.createdAt = data?.createdAt ? new Date(data.createdAt) : new Date();
        this.updatedAt = data?.updatedAt ? new Date(data.updatedAt) : new Date();
        this.createdBy = data?.createdBy ?? "";
        this.updatedBy = data?.updatedBy ?? "";
        
        // Nuevos campos
        this.images = data?.images ?? [];
        this.metadata = data?.metadata ?? "{}";
    }

    getSpanishName(propertyName: string): string {
        if (this.spanishNames && this.spanishNames[propertyName]) {
            return this.spanishNames[propertyName];
        }
        return propertyName;
    }

    toPlainObject(): Record<string, string | number | Date | boolean | DeviceImage[] | string | undefined> {
        return {
            id: this.id,
            code: this.code,
            locationId: this.locationId,
            name: this.name,
            extendedName: this.extendedName,
            description: this.description,
            slug: this.slug,
            initDate: this.initDate,
            active: this.active,
            lat: this.lat,
            lng: this.lng,
            altitude: this.altitude,
            periodicity: this.periodicity,
            groupId: this.groupId,
            alarmed: this.alarmed,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            createdBy: this.createdBy,
            updatedBy: this.updatedBy,
            images: this.images,
            metadata: this.metadata,
        };
    }

    // Método para obtener objeto sin ID (útil para creación)
    toCreateObject(): Record<string, string | number | Date | boolean | DeviceImage[] | string | undefined> {
        const obj = this.toPlainObject();
        delete obj.id; // No enviar ID en creación
        return obj;
    }

    // Helper methods para calcular datos dinámicos basados en deviceSamples
    static calculateGrowthPercentage(deviceSamples: DeviceSample[]): number {
        if (!deviceSamples || deviceSamples.length === 0) return 0;
        const latestMeasurement = deviceSamples[deviceSamples.length - 1];
        return latestMeasurement?.growthPercent || 0;
    }

    static calculateAverageGrowth(deviceSamples: DeviceSample[]): number {
        if (!deviceSamples || deviceSamples.length === 0) return 0;
        const totalGrowth = deviceSamples.reduce((sum, m) => sum + (m.growthPercent || 0), 0);
        return Math.round((totalGrowth / deviceSamples.length) * 100) / 100;
    }

    static getLastMeasurementDate(deviceSamples: DeviceSample[]): Date | null {
        if (!deviceSamples || deviceSamples.length === 0) return null;
        const latestMeasurement = deviceSamples[deviceSamples.length - 1];
        return latestMeasurement?.createdAt ? new Date(latestMeasurement.createdAt) : null;
    }

    static getTotalMeasurements(deviceSamples: DeviceSample[]): number {
        return deviceSamples ? deviceSamples.length : 0;
    }

    static getCompletedMeasurements(deviceSamples: DeviceSample[]): number {
        if (!deviceSamples) return 0;
        return deviceSamples.filter(m => m.managed).length;
    }

    static getHealthScoreAverage(deviceSamples: DeviceSample[]): number {
        if (!deviceSamples || deviceSamples.length === 0) return 0;
        const managedMeasurements = deviceSamples.filter(m => m.managed);
        if (managedMeasurements.length === 0) return 0;
        
        const totalScore = managedMeasurements.reduce((sum, m) => sum + (m.rootHealthScore || 0), 0);
        return Math.round((totalScore / managedMeasurements.length) * 100) / 100;
    }

    static getLastObservation(deviceSamples: DeviceSample[]): string {
        if (!deviceSamples || deviceSamples.length === 0) return 'Sin observaciones';
        
        // Buscar la última medición con observación
        for (let i = deviceSamples.length - 1; i >= 0; i--) {
            if (deviceSamples[i].observation && deviceSamples[i].observation.trim() !== '') {
                return deviceSamples[i].observation;
            }
        }
        
        return 'Sin observaciones registradas';
    }

    static getCompletionPercentage(deviceSamples: DeviceSample[], expectedMeasurements: number): number {
        if (expectedMeasurements <= 0) return 0;
        const actualMeasurements = this.getTotalMeasurements(deviceSamples);
        return Math.min(Math.round((actualMeasurements / expectedMeasurements) * 100), 100);
    }

    static formatLastUpdate(deviceSamples: DeviceSample[]): string {
        const lastDate = this.getLastMeasurementDate(deviceSamples);
        if (!lastDate) return 'No disponible';

        const now = new Date();
        const diffMs = now.getTime() - lastDate.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
            return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
        } else if (diffHours > 0) {
            const remainingMinutes = diffMinutes % 60;
            return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''} con ${remainingMinutes} minutos`;
        } else {
            return `hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
        }
    }
}
